import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { validateUsername } from "@/utils/security";

interface UserProfile {
  id: string;
  username: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
  fantasy_testing_opt_in: boolean;
  fantasy_testing_opted_at: string | null;
}

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchProfile = async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, created_at, updated_at, fantasy_testing_opt_in, fantasy_testing_opted_at')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        // Phone is column-locked; fetch via SECURITY DEFINER RPC
        let phone: string | null = null;
        const { data: bank } = await supabase.rpc('get_my_profile_bank');
        if (bank && bank[0]) phone = (bank[0] as any).phone ?? null;
        setProfile({ ...(data as any), phone } as UserProfile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUsername = async (newUsername: string) => {
    if (!user) return { error: 'No user logged in' };

    // Validate username format
    const usernameValidation = validateUsername(newUsername);
    if (!usernameValidation.isValid) {
      return { error: usernameValidation.error };
    }

    const trimmedUsername = newUsername.trim();

    try {
      // Check if username already exists for another user
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', trimmedUsername)
        .neq('id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        return { error: 'Failed to check username availability' };
      }

      if (existingUser) {
        return { error: 'Uživatelské jméno již existuje' };
      }

      const { error } = await supabase
        .from('profiles')
        .update({ username: trimmedUsername })
        .eq('id', user.id);

      if (error) {
        return { error: error.message };
      }

      // Update local state
      setProfile(prev => prev ? { ...prev, username: trimmedUsername } : null);
      return { error: null };
    } catch (error) {
      return { error: 'Failed to update username' };
    }
  };

  const updatePhone = async (newPhone: string) => {
    if (!user) return { error: 'No user logged in' };

    const trimmed = newPhone.trim();
    // Basic validation: allow empty (to clear), or digits/spaces/+/- up to 20 chars
    if (trimmed && !/^[\d\s\+\-()]{3,20}$/.test(trimmed)) {
      return { error: 'Neplatný formát telefonního čísla' };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ phone: trimmed || null } as any)
      .eq('id', user.id);

    if (error) return { error: error.message };

    setProfile(prev => prev ? { ...prev, phone: trimmed || null } : null);
    return { error: null };
  };

  const updateFantasyTestingOptIn = async (optIn: boolean) => {
    if (!user) return { error: 'No user logged in' };

    const { error } = await supabase
      .from('profiles')
      .update({ fantasy_testing_opt_in: optIn })
      .eq('id', user.id);

    if (error) {
      return { error: error.message };
    }

    // Znovu načteme profil, aby se propsaly hodnoty včetně timestampu z triggeru
    await fetchProfile();
    return { error: null };
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    profile,
    loading,
    updateUsername,
    updatePhone,
    updateFantasyTestingOptIn,
    refetch: fetchProfile
  };
};
